import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap } from '../lib/api-envelope';
import { updateQueryDataMapped } from '../lib/queryClient';
import { mergeCrewResponse, type CrewPatchBody } from '../lib/dispatch-crew-phase';
import type { DispatchCrewMember } from '../types/dispatch';
import type { DispatchJobsCache } from './dispatchJobsQuery';

export interface CrewWriteVars {
  jobId: string;
  body: CrewPatchBody;
  /** Hands the board knows but the job may not yet — the CREW column's entry
   *  for a hand being added, so the card keeps their specialty badge. */
  known?: DispatchCrewMember[];
}

type BoardCache = DispatchJobsCache | undefined;

/**
 * PATCH /dispatch/jobs/:id/crew, and put the crew the RESPONSE returns on the
 * card at once.
 *
 * The response carries each hand's phase as the database now holds it (API
 * #730), so the card redraws from what was written rather than from what the
 * board guessed it would be. The board query is still invalidated afterwards
 * for the fields the response does not carry. Every cached board is updated —
 * the key carries the search and filters, and the same job can be in several.
 *
 * Errors are the caller's: the drop handlers word their own toasts.
 */
export function useCrewWriteMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ jobId, body }: CrewWriteVars) => {
      const res = await api.dispatch.updateCrew(jobId, body);
      return unwrap<{ crew?: unknown }>(res.data)?.crew;
    },
    onSuccess: (responseCrew, { jobId, known }) => {
      for (const [key] of queryClient.getQueriesData({ queryKey: ['dispatch-jobs'] })) {
        updateQueryDataMapped<BoardCache>(queryClient, key, (prev) => {
          if (!prev || !Array.isArray(prev.jobs)) return prev;
          return {
            ...prev,
            jobs: prev.jobs.map((j) => {
              if (j.id !== jobId) return j;
              const crew = mergeCrewResponse(j.crew ?? [], responseCrew, known);
              return crew ? { ...j, crew } : j;
            }),
          };
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch-jobs'] });
    },
  });
}
